import { useParams, Link, Navigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { blogPosts, getPostsForRegion } from "@/lib/blog-data";
import { ALL_LOCATIONS } from "@/lib/locations";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Calendar, User, Clock, ChevronLeft, Facebook, Twitter, Link as LinkIcon, ArrowRight } from "lucide-react";
import { ShareBar } from "@/components/ShareBar";
import { PricingCalculator } from "@/components/PricingCalculator";
import { BlogLeadCapture } from "@/components/BlogLeadCapture";
import { CostOfSuppliesInfographic } from "@/components/CostOfSuppliesInfographic";
import { MaidServiceChecklistDownload } from "@/components/MaidServiceChecklistDownload";
import { MaidVsHouseCleaningInfographic } from "@/components/MaidVsHouseCleaningInfographic";
import { CleaningHabitTrackerDownload } from "@/components/CleaningHabitTrackerDownload";
import { StandardVsDeepSlider } from "@/components/StandardVsDeepSlider";
import { CleaningFrequencyPlanner } from "@/components/CleaningFrequencyPlanner";
import { BlogFAQ } from "@/components/BlogFAQ";

import { useState, useEffect } from "react";

const BlogPost = () => {
  const { slug } = useParams();
  const { toast } = useToast();
  const [showStickyCTA, setShowStickyCTA] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCTA(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  let post = blogPosts.find((p) => p.slug === slug);
  
  if (!post) {
    for (const loc of ALL_LOCATIONS) {
      const localPosts = getPostsForRegion(loc.name, loc.regionSlug);
      const found = localPosts.find(p => p.slug === slug);
      if (found) {
        post = found;
        break;
      }
    }
  }

  if (!post) {
    return <Navigate to="/404" />;
  }

  const otherPosts = blogPosts.filter((p) => p.slug !== slug).slice(0, 2);

  const headings: { id: string; title: string }[] = [];
  const processedContent = post.content.replace(/<h2>(.*?)<\/h2>/g, (match, title) => {
    const cleanTitle = title.replace(/<[^>]+>/g, '').trim();
    const id = cleanTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    headings.push({ id, title: cleanTitle });
    return `<h2 id="${id}" class="scroll-m-24">${title}</h2>`;
  });

  return (
    <div className="flex min-h-screen flex-col">
      <SEO 
        title={`${post.title} — Uplevel Cleaning Sacramento`}
        description={post.excerpt}
        keywords={`${post.category.toLowerCase()}, cleaning tips sacramento, uplevel cleaning`}
        articleData={{
          title: post.title,
          description: post.excerpt,
          image: post.image,
          datePublished: new Date(post.date).toISOString(),
          author: post.author
        }}
      />
      <Navbar />
      <ShareBar title={post.title} variant="sticky-side" />
      <main className="flex-1 relative">
        {showStickyCTA && (
          <div className="fixed top-0 left-0 right-0 z-40 bg-primary text-primary-foreground py-3 px-4 shadow-md animate-in slide-in-from-top-4 fade-in duration-300 md:hidden">
            <div className="container flex items-center justify-between">
              <span className="text-sm font-bold truncate mr-4">Ready for a spotless home?</span>
              <Button asChild size="sm" variant="secondary" className="whitespace-nowrap shrink-0">
                <a href="/book?view=custom-plan">Book Now</a>
              </Button>
            </div>
          </div>
        )}
        {showStickyCTA && (
          <div className="fixed top-20 left-0 right-0 z-40 bg-primary text-primary-foreground py-3 px-4 shadow-md animate-in slide-in-from-top-4 fade-in duration-300 hidden md:block">
            <div className="container flex items-center justify-between max-w-4xl">
              <span className="text-base font-bold">Ready for a spotless home? Get your exact price in 60 seconds.</span>
              <Button asChild size="sm" variant="secondary" className="whitespace-nowrap">
                <a href="/book?view=custom-plan">Check Price & Book</a>
              </Button>
            </div>
          </div>
        )}
        <article className="py-20">
          <div className="container max-w-4xl">
            <Button asChild variant="ghost" className="mb-8 -ml-4 text-muted-foreground hover:text-primary">
              <Link to="/blog">
                <ChevronLeft className="mr-2 h-4 w-4" />
                Back to Blog
              </Link>
            </Button>

            <div className="mb-12">
              <div className="flex items-center gap-2 mb-6">
                <span className="bg-primary/10 text-primary text-[10px] uppercase font-black tracking-widest px-3 py-1 rounded-full">
                  {post.category}
                </span>
                <span className="text-muted-foreground text-sm">• {post.readTime}</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-8 leading-tight">
                {post.title}
              </h1>
              <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground border-y py-6">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-xs">
                    UC
                  </div>
                  <span>{post.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{post.date}</span>
                </div>
              </div>
            </div>

            <div className="aspect-video rounded-3xl overflow-hidden mb-12 shadow-2xl">
              <img 
                src={post.image} 
                alt={post.title} 
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>

            {headings.length > 0 && (
              <div className="bg-muted/30 p-6 rounded-2xl mb-12 border border-border">
                <h3 className="font-bold text-lg mb-4">Table of Contents</h3>
                <ul className="space-y-3">
                  {headings.map(h => (
                    <li key={h.id}>
                      <a href={`#${h.id}`} className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary/50" />
                        {h.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div 
              className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-foreground prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-foreground prose-a:text-primary hover:prose-a:underline"
              dangerouslySetInnerHTML={{ __html: processedContent }}
            />

            {(post.slug.includes("house-cleaning-cost") || post.slug.includes("pricing") || post.slug.includes("worth-it")) && (
              <CostOfSuppliesInfographic />
            )}

            {post.slug.includes("deep-cleaning-vs-standard-cleaning") && (
              <StandardVsDeepSlider />
            )}

            {post.slug.includes("maid-vs-house-cleaner") && (
              <MaidVsHouseCleaningInfographic />
            )}

            {post.slug.includes("keep-house-clean-between-cleanings") && (
              <CleaningHabitTrackerDownload />
            )}

            {post.slug.includes("how-often-should-you-clean-your-house") && (
              <div className="my-12">
                <CleaningFrequencyPlanner />
              </div>
            )}

            {post.slug.includes("house-cleaning-cost") && (
              <div className="mt-12">
                <PricingCalculator />
                <BlogLeadCapture />
              </div>
            )}

            <ShareBar title={post.title} />

            <BlogFAQ />

            <div className="mt-16 pt-12 border-t">
              <h3 className="text-2xl font-bold mb-8">Explore Our Services</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <Link to="/services/standard-clean" className="p-6 rounded-2xl border bg-card hover:border-primary hover:shadow-elegant transition-all text-center group">
                  <h4 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">Standard Cleaning</h4>
                  <p className="text-sm text-muted-foreground">Perfect for regular upkeep and maintaining a spotless home.</p>
                </Link>
                <Link to="/services/deep-clean" className="p-6 rounded-2xl border bg-card hover:border-primary hover:shadow-elegant transition-all text-center group">
                  <h4 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">Deep Cleaning</h4>
                  <p className="text-sm text-muted-foreground">Detailed top-to-bottom clean for homes that need extra care.</p>
                </Link>
                <Link to="/services/move-in-out" className="p-6 rounded-2xl border bg-card hover:border-primary hover:shadow-elegant transition-all text-center group">
                  <h4 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">Move-In / Move-Out</h4>
                  <p className="text-sm text-muted-foreground">Comprehensive cleaning for empty homes to ensure deposit return.</p>
                </Link>
              </div>
            </div>

            <div className="mt-16 pt-12 border-t">
              <h3 className="text-2xl font-bold mb-8">You might also like</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {otherPosts.map((other) => (
                  <Link key={other.slug} to={`/blog/${other.slug}`} className="group">
                    <div className="aspect-video rounded-2xl overflow-hidden mb-4">
                      <img src={other.image} alt={other.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <h4 className="text-xl font-bold group-hover:text-primary transition-colors leading-tight">
                      {other.title}
                    </h4>
                    <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                      <span>{other.date}</span>
                      <span>•</span>
                      <span>{other.readTime}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Floating Book Now CTA */}
        <div className="sticky bottom-6 mx-auto max-w-3xl z-40 px-4 pb-6 animate-in slide-in-from-bottom-10 fade-in duration-700">
          <a href="/book?view=custom-plan" className="flex items-center justify-between bg-primary text-primary-foreground p-4 md:p-6 rounded-2xl shadow-2xl hover:bg-primary-hover transition-all group border-2 border-white/20">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-3 rounded-full hidden sm:block">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-lg leading-tight">Ready for a spotless home?</h4>
                <p className="text-primary-foreground/80 text-sm">Get your instant flat-rate price and book in 60 seconds.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 font-bold whitespace-nowrap">
              Book Now <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </a>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;
